import { isNil, pickBy } from 'lodash-es'
import { createContext, useContext } from 'react'
import { create, useStore } from 'zustand'
import { useShallow } from 'zustand/shallow'

import type { FullShipment, Quote } from '@fv/client-types'
import type { LoadTemplateDTO, Workflow } from '@fv/models'

import { useQuoteSettings } from '@/hooks/settings'

import {
  defaultLoadHandlingUnit,
  toLoadHandlingUnitFormModel,
  useLoadHandlingUnitFormModels,
} from '../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../commodities/load-items/types'
import {
  buildShipmentCustomsBrokerFormModel,
  defaultFormBroker,
  type ShipmentCustomsBrokerFormModel,
} from '../customsBroker/brokerTypes'
import {
  buildEmergencyContactFormModel,
  type EmergencyContactFormModel,
} from '../emergencyContact/contactTypes'
import {
  applyTemplateToStops,
  clearTemplateFromStops,
} from '../load-templates/utils'
import { copyShipmentLocations } from '../quote/quoteFuncs'
import { templateItemToLoadItem } from '../settings/shipment-templates/utils'
import {
  buildShipmentEquipmentFormModel,
  type ShipmentEquipmentFormModel,
} from '../shipment-equipment-form/types'
import { useDefaultShipmentEquipmentFormModel } from '../shipment-equipment-form/utils'
import {
  defaultDestination,
  defaultOrigin,
  type ShipmentLocationFormModel,
} from '../shipment-location-form/types'
import { useShipLocFormModelBuilder } from '../shipment-location-form/useLocFormModelBuilder'

export type ShipmentFormType = 'create' | 'book' | 'edit'

type ShipmentFormState = {
  initialLoad?: FullShipment
  quote?: Quote
  items: LoadHandlingUnitFormModel[]
  locations: ShipmentLocationFormModel[]
  equipment: ShipmentEquipmentFormModel
  emergencyContact: EmergencyContactFormModel
  customsBrokers: ShipmentCustomsBrokerFormModel
  bolNum: string
  glCode?: string
  schedulePickup?: boolean
  isPickupOnly: boolean
  type: ShipmentFormType
  workflow: Workflow
  selectedTemplate?: LoadTemplateDTO | null
  itemsFormKey: number
}
type Updater<T> = T | ((prev: T) => T)

type ShipmentFormActions = {
  actions: {
    setItems: (items: LoadHandlingUnitFormModel[]) => void
    setLocations: (locations: Updater<ShipmentLocationFormModel[]>) => void
    setEquipment: (equipment: Updater<ShipmentEquipmentFormModel>) => void
    setEmergencyContact: (emergencyContact: EmergencyContactFormModel) => void
    setCustomsBrokers: (customsBrokers: ShipmentCustomsBrokerFormModel) => void
    setBolNum: (bolNum: string) => void
    setGlCode: (glCode: string) => void
    setSchedulePickup: (schedulePickup: boolean) => void
    setPickupOnly: (isPickupOnly: boolean) => void
    setWorkflow: (workflow: Workflow) => void
    applyLoadTemplate: (template: LoadTemplateDTO) => void
    clearLoadTemplate: () => void
    clear: () => void
  }
}
type ShipmentFormStoreContents = ShipmentFormState & ShipmentFormActions

export type ShipmentFormStore = ReturnType<typeof createShipmentFormStore>
export const ShipmentFormContext = createContext<ShipmentFormStore | null>(null)

function useShipmentFormStore<T>(
  selector?: (state: ShipmentFormStoreContents) => T,
) {
  const store = useContext(ShipmentFormContext)

  if (!store) {
    throw new Error('Missing ShipmentFormContext.Provider in the tree')
  }
  return useStore(store, selector)
}

export const useShipmentFormActions = () => {
  return useShipmentFormStore(s => s.actions)
}

export const useShipmentFormState = () => {
  return useShipmentFormStore(useShallow(({ actions, ...state }) => state))
}

type CreateProps = {
  workflow: Workflow
}

type BookProps = {
  load: FullShipment
  quote: Quote
}

type EditProps = {
  load: FullShipment
}

type CopyProps = {
  copyFrom: FullShipment
}
export type StoreInitProps = CreateProps | BookProps | EditProps | CopyProps

const isCopyForm = (props: StoreInitProps): props is CopyProps =>
  'copyFrom' in props

const isBookForm = (props: StoreInitProps): props is BookProps => {
  return 'load' in props && 'quote' in props
}

const isEditForm = (props: StoreInitProps): props is EditProps => {
  return 'load' in props && !isBookForm(props)
}
const createShipmentFormStore = (
  state: ShipmentFormState,
  buildEmpty: (workflow: Workflow) => ShipmentFormState,
) => {
  const clearTemplateState = (
    prev: ShipmentFormState,
  ): Partial<ShipmentFormState> => {
    const template = prev.selectedTemplate
    if (!template) return {}
    const empty = buildEmpty(prev.workflow)

    return {
      selectedTemplate: null,
      locations: clearTemplateFromStops(prev.locations, template, (_, type) =>
        type === 'origin' ? defaultOrigin() : defaultDestination(),
      ),
      ...(template.items?.length && {
        items: empty.items,
        itemsFormKey: prev.itemsFormKey + 1,
      }),
      ...(prev.workflow === 'truckload' &&
        template.equipment?.type && { equipment: empty.equipment }),
      ...(template.glCode && { glCode: undefined }),
    }
  }

  return create<ShipmentFormStoreContents>((set, get) => ({
    ...state,
    actions: {
      clear: () =>
        set(s => ({
          ...buildEmpty(s.workflow),
          itemsFormKey: s.itemsFormKey + 1,
        })),
      applyLoadTemplate: template =>
        set(current => {
          const prev = { ...current, ...clearTemplateState(current) }
          const dropSequence = prev.locations.length - 1
          const templateEquipment = pickBy(
            template.equipment,
            v => !isNil(v),
          ) as Partial<ShipmentEquipmentFormModel>

          return {
            selectedTemplate: template,
            locations: applyTemplateToStops(prev.locations, template),
            items: template.items?.length
              ? template.items.map(item =>
                  toLoadHandlingUnitFormModel(
                    templateItemToLoadItem(item, dropSequence),
                  ),
                )
              : prev.items,
            itemsFormKey: template.items?.length
              ? prev.itemsFormKey + 1
              : prev.itemsFormKey,
            equipment:
              prev.workflow === 'truckload' && templateEquipment.type
                ? { ...prev.equipment, ...templateEquipment }
                : prev.equipment,
            glCode: template.glCode || prev.glCode,
          }
        }),
      clearLoadTemplate: () => set(clearTemplateState),
      setItems: (items: LoadHandlingUnitFormModel[]) => set({ items }),
      setLocations: locations =>
        set(s => ({
          locations:
            typeof locations === 'function'
              ? locations(s.locations)
              : locations,
        })),
      setEquipment: equipment =>
        set(s => ({
          equipment:
            typeof equipment === 'function'
              ? equipment(s.equipment)
              : equipment,
        })),
      setEmergencyContact: (emergencyContact: EmergencyContactFormModel) =>
        set({ emergencyContact }),
      setCustomsBrokers: (customsBrokers: ShipmentCustomsBrokerFormModel) =>
        set({ customsBrokers }),
      setBolNum: (bolNum: string) => set({ bolNum }),
      setGlCode: (glCode: string) => set({ glCode }),
      setSchedulePickup: (schedulePickup: boolean) => set({ schedulePickup }),
      setPickupOnly: (isPickupOnly: boolean) => set({ isPickupOnly }),
      setWorkflow: (workflow: Workflow) => {
        const { equipment, items, schedulePickup } = buildEmpty(workflow)
        const resetItems = get().workflow === 'parcel' || workflow === 'parcel'
        const locations =
          workflow === 'truckload'
            ? get().locations
            : get()
                .locations.filter(l => l.type !== 'stop')
                .map((l, sequence) => ({ ...l, sequence }))

        set({
          equipment,
          workflow,
          schedulePickup,
          locations,
          selectedTemplate: null,
          ...(resetItems && { items }),
        })
      },
    },
  }))
}

const useEmptyStateBuilder = (): ((
  workflow: Workflow,
) => ShipmentFormState) => {
  const buildEquipment = useDefaultEquipmentBuilder()
  return (workflow: Workflow) => ({
    initialLoad: undefined,
    quote: undefined,
    items: [defaultLoadHandlingUnit(workflow)],
    locations: [{ ...defaultOrigin() }, defaultDestination()],
    equipment: buildEquipment(workflow),
    emergencyContact: { ...defaultEmergencyContact },
    customsBrokers: { ...defaultFormBroker },
    bolNum: '',
    isPickupOnly: false,
    type: 'create',
    workflow,
    selectedTemplate: null,
    itemsFormKey: 0,
    ...(workflow === 'parcel' && {
      schedulePickup: false,
    }),
  })
}

export const useCreateShipmentFormStore = (props: StoreInitProps) => {
  const isBook = isBookForm(props)
  const isEdit = isEditForm(props)
  const sourceLoad = isCopyForm(props)
    ? {
        ...props.copyFrom,
        locations: copyShipmentLocations(props.copyFrom.locations),
      }
    : isBook || isEdit
      ? props.load
      : undefined
  const workflow = sourceLoad?.workflow ?? (props as CreateProps).workflow
  const quoteSettingsQuery = useQuoteSettings(workflow)
  const buildEmptyState = useEmptyStateBuilder()
  const locBuilder = useShipLocFormModelBuilder(sourceLoad ?? { workflow })

  const loadHandlingUnits = useLoadHandlingUnitFormModels(sourceLoad)

  return {
    isLoading:
      loadHandlingUnits.isLoading ||
      locBuilder.isLoading ||
      quoteSettingsQuery.isLoading,
    create: () => {
      // Pickup-only bookings have no items yet; start the BOL with a blank one.
      const items = loadHandlingUnits.data?.length
        ? loadHandlingUnits.data
        : [defaultLoadHandlingUnit(workflow)]
      return createShipmentFormStore(
        {
          ...buildEmptyState(workflow),
          ...(sourceLoad && {
            locations: sourceLoad.locations.map(l => locBuilder.build(l)),
            items,
            glCode: sourceLoad.glCode,
            schedulePickup: sourceLoad.schedulePickup,
            isPickupOnly:
              sourceLoad.workflow === 'truckload' && !sourceLoad.items?.length,
            equipment: buildShipmentEquipmentFormModel(sourceLoad),
            emergencyContact: buildEmergencyContactFormModel(
              sourceLoad.emergencyContact,
            ),
            customsBrokers: sourceLoad.customsBroker?.map(b =>
              buildShipmentCustomsBrokerFormModel(b),
            )[0] ?? {
              ...defaultFormBroker,
            },
          }),
          ...((isBook || isEdit) && {
            initialLoad: props.load,
            bolNum: props.load.bol?.bolNumber,
          }),
          quote: isBook ? props.quote : undefined,
          type: isBook ? 'book' : isEdit ? 'edit' : 'create',
        },
        buildEmptyState,
      )
    },
  }
}

const defaultEmergencyContact: EmergencyContactFormModel = {
  contractNumber: '',
  name: '',
  phone: '',
  phoneExt: '',
}

export const useDefaultEquipmentBuilder = useDefaultShipmentEquipmentFormModel
